/**
 * Structured-content color tokens, ported from the MUI implementation.
 *
 * Values resolve packages/ui/src/Theme/index.ts:309-405 or preserve literals in
 * the legacy maps. `undefined` means paint no background, as legacy does for an
 * unresolvable palette path.
 */
export type ColorToken =
  | 'blue'
  | 'blue-dark'
  | 'blue-darkest'
  | 'blue-extra-light'
  | 'blue-light'
  | 'blue-lighter'
  | 'blue-lighter-2'
  | 'blue-lightest'
  | 'bone'
  | 'brown'
  | 'brown-dark'
  | 'brown-darkest'
  | 'brown-light'
  | 'brown-lighter'
  | 'brown-lighter-2'
  | 'brown-lighter-3'
  | 'brown-lighter-4'
  | 'green'
  | 'green-dark'
  | 'green-darkest'
  | 'green-extra-light'
  | 'green-light'
  | 'green-lighter'
  | 'green-lighter-2'
  | 'green-lightest'
  | 'neutral-dark'
  | 'neutral-darker'
  | 'neutral-darker-2'
  | 'neutral-darkest'
  | 'neutral-extra-dark'
  | 'neutral-light'
  | 'neutral-lighter'
  | 'neutral-lighter-2'
  | 'neutral-lightest'
  | 'nw-green'
  | 'nw-logo-green'
  | 'nw-logo-green-dark'
  | 'nw-logo-green-light'
  | 'red'
  | 'red-dark'
  | 'red-darker'
  | 'red-extra-light'
  | 'red-light'
  | 'red-lighter'
  | 'red-lighter-2'
  | 'red-lightest'
  | 'teal'
  | 'teal-light'
  | 'transparent'
  | 'white'
  | 'yellow'
  | 'yellow-dark'
  | 'yellow-darkest'
  | 'yellow-extra-light'
  | 'yellow-light'
  | 'yellow-lighter'
  | 'yellow-lighter-2'
  | 'yellow-lightest';

export const COLUMNS_COLORS: Record<ColorToken, string | undefined> = {
  transparent: 'rgba(255,255,255,0)', // literal in legacy Columns map; Safari handling

  green: '#008254', // greens.main
  'green-light': '#40AD87', // primary.light
  'green-lighter': '#81D6B8', // literal in legacy Columns map
  'green-lighter-2': '#B7ECDA', // greens.lighter
  'green-lightest': '#E3FAF2', // greens.lightest
  'green-extra-light': '#EFFCF8', // greens.extraLight
  'green-dark': '#006642', // greens.dark
  'green-darkest': '#00482F', // greens.darkest
  'nw-green': '#8ccb32', // literal in legacy Columns map
  'nw-logo-green': '#00AE4D', // literal in legacy Columns map
  'nw-logo-green-dark': '#00682C', // literal in legacy Columns map
  'nw-logo-green-light': '#8DC63F', // literal in legacy Columns map

  blue: '#005FB9', // blues.main
  'blue-light': '#4B99E6', // literal in legacy Columns map
  'blue-lighter': '#9DCDFA', // blues.lighter
  'blue-lighter-2': '#C8E4FE', // literal in legacy Columns map
  'blue-lightest': '#EEF7FF', // blues.lightest
  'blue-extra-light': '#DEEFFF', // blues.extraLight
  'blue-dark': '#02427E', // blues.dark
  'blue-darkest': '#02143A', // literal in legacy Columns map

  red: '#DE2828', // reds.main
  'red-light': '#FF5D5E', // reds.light
  'red-lighter': '#FF9090', // reds.lighter
  'red-lighter-2': '#FFBFBF', // literal in legacy Columns map
  'red-lightest': '#FFE8E8', // reds.lightest
  'red-extra-light': '#FFF7F7', // literal in legacy Columns map
  'red-dark': '#B40000', // reds.dark
  'red-darker': '#7A0000', // literal in legacy Columns map

  yellow: '#FFC764', // yellows.main
  'yellow-light': '#FBD083', // yellows.light
  'yellow-lighter': '#FFE2AE', // yellows.lighter
  'yellow-lighter-2': undefined, // yellows.lighter-2 (invalid legacy Columns path)
  'yellow-lightest': '#FFF7EA', // yellows.lightest
  'yellow-extra-light': '#FFFBF4', // literal in legacy Columns map
  'yellow-dark': '#EFA92E', // yellows.dark
  'yellow-darkest': '#B07000', // literal in legacy Columns map

  white: '#ffffff', // common.white
  'neutral-darkest': '#000', // common.black
  'neutral-darker-2': '#3D4045', // neutral.darker
  'neutral-darker': '#64666A', // neutral.dark
  'neutral-dark': '#8B8C8F', // neutral.main
  'neutral-extra-dark': '#3D4045', // neutral.darker
  'neutral-light': '#B1B3B5', // neutral.light
  'neutral-lighter': '#D8D9DA', // neutral.lighter
  'neutral-lighter-2': '#EBEBEC', // literal in legacy Columns map
  'neutral-lightest': '#F7F7F7', // literal in legacy Columns map

  bone: '#F4EEE6', // literal in legacy Columns map

  brown: '#B96C4F', // literal in legacy Columns map
  'brown-light': '#D58B70', // literal in legacy Columns map
  'brown-lighter': '#EDBEAD', // literal in legacy Columns map
  'brown-lighter-2': '#FFEAE2', // literal in legacy Columns map
  'brown-lighter-3': '#FEF4F1', // literal in legacy Columns map
  'brown-lighter-4': '#FFFAF9', // literal in legacy Columns map
  'brown-dark': '#91472B', // literal in legacy Columns map
  'brown-darkest': '#652812', // literal in legacy Columns map

  teal: '#005FB9', // blues.main
  'teal-light': '#EEF7FF', // blues.lightest
};

export const WELL_COLORS: Record<ColorToken, string | undefined> = {
  ...COLUMNS_COLORS,
  transparent: 'rgba(255,255,255,0)', // literal in legacy Well map; Safari handling

  'green-light': '#E3FAF2', // greens.light
  'green-lighter': '#B7ECDA', // greens.lighter
  'green-lighter-2': '#B7ECDA', // greens.lighter
  'green-lightest': '#E3FAF2', // greens.lightest
  'green-extra-light': '#EFFCF8', // greens.extraLight
  'green-dark': '#006642', // greens.dark
  'green-darkest': '#00482F', // greens.darkest
  'nw-green': '#8ccb32', // literal in legacy Well map
  'nw-logo-green': '#00AE4D', // literal in legacy Well map
  'nw-logo-green-light': '#8DC63F', // literal in legacy Well map

  'blue-light': '#54A0E8', // blues.light
  'blue-lighter': '#9DCDFA', // blues.lighter
  'blue-lightest': '#EEF7FF', // blues.lightest
  'blue-dark': '#02427E', // blues.dark

  'red-light': '#FF5D5E', // reds.light
  'red-lighter': '#FF9090', // reds.lighter
  'red-lightest': '#FFE8E8', // reds.lightest
  'red-dark': '#B40000', // reds.dark

  yellow: '#FFC764', // yellows.main
  'yellow-light': '#FBD083', // yellows.light
  'yellow-lighter': '#FFE2AE', // yellows.lighter
  'yellow-lightest': '#FFF7EA', // yellows.lightest
  'yellow-dark': '#EFA92E', // yellows.dark
  'yellow-darkest': '#B07000', // yellows.darkest

  white: '#ffffff', // common.white
  'neutral-darkest': '#2A2A2A', // neutral.darkest
  'neutral-darker': '#3D4045', // neutral.darker
  'neutral-dark': '#64666A', // neutral.dark
  'neutral-light': '#B1B3B5', // neutral.light
  'neutral-lighter': '#D8D9DA', // neutral.lighter
  'neutral-lightest': '#F5F5F5', // neutral.lightest

  bone: '#F4EEE6', // bone.main

  teal: '#005FB9', // blues.main
  'teal-light': '#EEF7FF', // blues.lightest
};
